import { MediaAPI } from '@business/api/media';
import { FolderBreadcrumbResponse } from '@business/entities/file';
import { MediaFolderResponse, MediaId, MediaItemType, MediaRequest, MediaResponse } from '@business/entities/media';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { MediaFolderId } from './../../entities/media/MediaResponse';

export class MediaBloc extends BaseBloc {
  private readonly itemsRepo = new Repository<MediaFolderResponse | MediaResponse>({
    getItemId: item => `${item.mediaType}-${item.id}`,
  });

  readonly items$ = this.itemsRepo.items$;
  readonly selectItem$ = this.itemsRepo.selectItem;
  readonly selectMedia = (hash: string) =>
    this.items$.pipe(
      map(items => R.find(items as MediaResponse[], i => i.hashKey === hash)),
      distinctUntilChanged(R.isEqual),
    );
  readonly breadcrumb$ = new BehaviorSubject<FolderBreadcrumbResponse[]>([]);

  onReset() {
    this.itemsRepo.reset();
  }

  fetchItems = (parentId?: MediaFolderId) => {
    return MediaAPI.getItems(parentId).pipe(
      map(data => {
        this.breadcrumb$.next(data.breadcrumbs);
        return data.items;
      }),
      this.itemsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_media_error') }),
    );
    //return MediaAPI.getItems(parentId).pipe(this.itemsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_files_error') }));
  };

  fetchFolders = () => {
    return MediaAPI.fetchFolders().pipe(handleMessage({ error: requestMessage('fetch_media_error') }));
  };

  moveItem = (itemId: string, type: MediaItemType, folderId: MediaFolderId, oldFolderId?: MediaFolderId) => {
    return MediaAPI.moveItem(itemId, type, folderId, oldFolderId).pipe(handleMessage({ error: requestMessage('fetch_media_error') }));
  };

  fetchMedia = () => {
    return MediaAPI.fetchMedia().pipe(this.itemsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_media_error') }));
  };

  fetchMediaByHash = (hash: string) => {
    return MediaAPI.fetchMediaByHash(hash).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_media_error') }),
    );
  };

  createFolder = (name: string, parentId?: MediaFolderId, files?: MediaId[]) => {
    return MediaAPI.createFolder(name, parentId, files).pipe(
      this.itemsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_folder'),
      }),
    );
  };

  updateFolder = (id: MediaFolderId, name: string, files?: MediaId[]) => {
    return MediaAPI.updateFolder(id, name, files).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('rename_folder'),
      }),
    );
  };
  deleteFolder = (id: MediaFolderId) => {
    return showDeleteConfirmation('Delete Folder', 'Do you really want to remove this folder?').pipe(
      switchMap(() => MediaAPI.deleteFolder(id)),
      this.itemsRepo.ops.removeOne(() => `${MediaItemType.folder}-${id}`),
      handleMessage({
        type: requestMessage('delete_folder'),
      }),
    );
  };

  createMedia = (data: MediaRequest, parentId?: MediaFolderId) => {
    return MediaAPI.createMedia(data, parentId).pipe(
      //   this.itemsRepo.ops.upsertOne(item => ({ item })),
      this.itemsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('create_media'),
      }),
    );
  };

  updateMedia = (id: MediaId, data: MediaRequest) => {
    return MediaAPI.updateMedia(id, data).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_media'),
      }),
    );
  };

  deleteMedia = (id: MediaId, folderId?: MediaFolderId) => {
    return showDeleteConfirmation('Delete Media', 'Do you really want to remove this media?').pipe(
      switchMap(() => MediaAPI.deleteMedia(id, folderId)),
      this.itemsRepo.ops.removeOne(() => `${MediaItemType.media}-${id}`),
      handleMessage({
        type: requestMessage('delete_media'),
      }),
    );
  };
}
